import type { Mutable } from 'utility-types'

import { delay } from '../../../utils'

import {
    type AppState,
    type AuthenticationInformation,
    ConnectwareError,
    isAuthenticated,
    selectAuthenticatedToken,
    selectAuthentication,
    selectAuthenticationExpiresAt,
    selectLoginForm,
    TokenExpiredError,
} from '../../../domain'

import { initialState } from '../..'
import { Usecase } from '../Usecase'

export enum LogoutReason {
    /**
     * User has manually logged out
     */
    MANUAL_LOGOUT = 'MANUAL_LOGOUT',

    /**
     * Logout is happening due to token being expired
     */
    TOKEN_EXPIRED = 'TOKEN_EXPIRED',
}

export abstract class LoginUsecase extends Usecase {
    /**
     * This checks if the user should be redirected, and then does it
     */
    private attemptRedirection (): void {
        const currentUrl = this.browserService.getRedirectionURL()

        if (!currentUrl) {
            return
        }

        try {
            this.browserService.redirect(currentUrl)
        } catch (e: unknown) {
            this.logger.error(e as ConnectwareError)
        }
    }

    private async setupExpirationTimers (): Promise<void> {
        const oldAuthentication = selectAuthentication(this.getState())
        if (!isAuthenticated(oldAuthentication)) {
            /** No need for timer, user is not authenticated */
            return
        }

        const expiresAt = selectAuthenticationExpiresAt(oldAuthentication)
        for (const { executionTimestamp, execute } of [
            /** Set expire soon to true when the warning period starts */
            {
                executionTimestamp: new Date(expiresAt.getTime() - this.configurationService.getLogoutWarningPeriod()),
                execute: () => this.setState({ authentication: { ...oldAuthentication, expiresSoon: true } }),
            },
            /** Log out at the expiration */
            {
                executionTimestamp: expiresAt,
                execute: () => this.logout(LogoutReason.TOKEN_EXPIRED),
            },
        ]) {
            await delay(executionTimestamp)

            if (selectAuthenticatedToken(this.getState()) !== oldAuthentication.token) {
                /** Authentication has changed, so stop everything */
                return
            }

            execute()
        }
    }

    protected persist (info: AuthenticationInformation | null): void {
        this.browserService.authenticate(info)

        if (info) {
            this.authenticationPersistenceService.persist(info)
        } else {
            this.authenticationPersistenceService.flush()
            this.tablePersistenceService.flush()
        }
    }

    protected updateAuthentication (authentication: AppState['authentication']): void {
        if (!ConnectwareError.is(authentication)) {
            this.persist(authentication)
        }

        /**
         * Finally, update the state and if the user is authenticated,
         * remove their password so it cannot be reused
         */
        const state: Mutable<Partial<AppState>> = { authentication }
        if (isAuthenticated(authentication)) {
            /** Reset everything but the username */
            state.loginForm = { ...selectLoginForm(initialState), username: authentication.username }
        }
        this.setState(state)

        void this.setupExpirationTimers()

        if (isAuthenticated(authentication)) {
            this.attemptRedirection()
        }
    }

    protected updateAuthenticationFromToken (token: string): Promise<void> {
        return this.authenticationService
            .fetchSession(token)
            .then((session) => this.updateAuthentication({ ...session, token, obsolete: null, expiresSoon: false }))
            .catch((error: ConnectwareError) => this.updateAuthentication(error))
    }

    protected logout (logoutReason: LogoutReason): void {
        const token = selectAuthenticatedToken(this.getState())

        if (token !== null) {
            this.authenticationService.flushAuthentication(token).catch((e: ConnectwareError) => this.logger.error(e))
        }

        this.persist(null)
        this.setState({ authentication: logoutReason === LogoutReason.TOKEN_EXPIRED ? new TokenExpiredError() : null })
    }
}
